import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Tooltip } from '@column/column-ui-kit';

import { PopoverEvents } from '~/components';
import { EventsIcon } from '~/elements/icons';
import { useSessionStore } from '~/stores/Session';

const Wrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: none;

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const Action = styled.div<{ $disabled?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  display: flex;
  gap: 4px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  align-items: center;
  position: relative;
  align-items: center;
  padding: 8px 12px;
  box-sizing: border-box;
  border-radius: 8px;
  background-color: transparent;
  box-shadow:
    0 0 0 1px ${({ theme }) => theme.gray.blendToBackground(150, 0)},
    0 0 0 var(--button-outline, 0px)
      var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(1000, 0)}) inset,
    0 0 0 0 transparent;
  transition:
    box-shadow 0.2s,
    background-color 0.2s;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    --icon-size: 16px;
    transition: stroke 0.2s;
  }

  ${({ $disabled }) =>
    !$disabled &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.secondary.blendToBackground(75)};
        box-shadow:
          0 0 0 1px ${({ theme }) => theme.gray.blendToBackground(150)} inset,
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.primary.blendToBackground(1000, 150)}),
          0 0 0 0 transparent;

        svg {
          --icon-color: ${({ theme }) => theme.secondary.blendToBackground(1000)};
        }

        ${({ theme }) =>
          theme.id !== 'default' &&
          css`
            background-color: ${() => theme.secondary.blendToBackground(20)};
            box-shadow:
              0 0 0 1px ${() => theme.gray.blendToBackground(100)} inset,
              0 0 0 var(--button-outline, 0px)
                var(--button-outline-color, ${() => theme.primary.blendToBackground(1000, 150)}),
              0 0 0 0 transparent;
          `};
      }
    `}
`;

const ActionLabel = styled.div`
  display: flex;
  flex: auto;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  transition: color 0.2s;
  overflow: hidden;
  white-space: nowrap;
  color: ${({ theme }) => theme.secondary.background};

  & > svg {
    --icon-size: 16px;
  }
`;

export const Events: React.FC = () => {
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const actionRef = useRef<HTMLDivElement>(null);
  const { currentPlatform, isAuthorized } = useSessionStore();

  const hasEventsAccess = isAuthorized({ permission: 'webhooks', permissionLevel: 'read' });

  const handleTriggerClick = useCallback(() => {
    if (!hasEventsAccess) {
      return;
    }
    setShowPopover((s) => !s);
  }, [hasEventsAccess]);

  const handlePopoverClose = useCallback(
    (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }
      if (actionRef.current && (event.target === actionRef.current || actionRef.current.contains(event.target))) {
        return;
      }

      setShowPopover(false);
    },
    [actionRef, currentPlatform?.isLiveEnabled]
  );

  return (
    <Tooltip
      content={!hasEventsAccess ? "You don't have permission to view events." : undefined}
      isOpen={hasEventsAccess ? false : undefined}
      placement="bottom"
    >
      <Wrapper $disabled={!hasEventsAccess}>
        <Action ref={actionRef} onClick={handleTriggerClick} $disabled={!hasEventsAccess}>
          <EventsIcon size={16} />
          <ActionLabel>Events</ActionLabel>
        </Action>
        {hasEventsAccess && <PopoverEvents show={showPopover} onClose={handlePopoverClose} />}
      </Wrapper>
    </Tooltip>
  );
};
